import {
  INPUT_REDUNDANCY,
  PLAYER_MAX_HEALTH,
  matchPhaseFromIndex,
  matchPhaseIndex,
  vec3,
  weaponFromIndex,
  weaponIndex,
  type HubGroupStatus,
  type InputCommand,
  type LobbyEntry,
  type MatchStatus,
  type PlayerId,
  type PlayerState,
  type SimEvents,
  type TeamId,
  type Vec3,
  type WeaponId,
} from '@game/shared';
import {
  Reader,
  Writer,
  packAngle,
  packPosition,
  unpackAngle,
  unpackPosition,
} from './codec.js';

/** Bump when any wire layout below changes; the server rejects mismatched clients. */
export const PROTOCOL_VERSION = 10;

export const ClientMessage = {
  Join: 0x01,
  Input: 0x02,
  Ping: 0x03,
  Chat: 0x04,
  SelectLoadout: 0x05,
  VoteMap: 0x06,
  PadReady: 0x07,
  Rematch: 0x08,
  LeaveMatch: 0x09,
  VoiceSignal: 0x0a,
  VoteDifficulty: 0x0b,
} as const;

export const ServerMessage = {
  Welcome: 0x81,
  Snapshot: 0x82,
  Events: 0x83,
  Pong: 0x84,
  PlayerJoined: 0x85,
  PlayerLeft: 0x86,
  Chat: 0x87,
  MatchState: 0x88,
  HubState: 0x89,
  VoiceSignal: 0x8a,
  Error: 0x8f,
} as const;

// ---------------------------------------------------------------------------
// Client -> Server
// ---------------------------------------------------------------------------

export interface JoinMessage {
  type: typeof ClientMessage.Join;
  protocol: number;
  /** Index into CHARACTERS — cosmetic only, clamped server-side. */
  character: number;
  name: string;
  /**
   * Opaque REST session token, or '' for a guest. Lets the server tie the
   * socket to an account — leaderboard credit and one-connection-per-account
   * both hang off this. Sent inside the first frame rather than in the URL so
   * it never lands in proxy access logs.
   */
  session: string;
}

export interface InputMessage {
  type: typeof ClientMessage.Input;
  /** Client tick the newest command belongs to. */
  tick: number;
  /** Newest first is not assumed — commands are ordered oldest to newest. */
  commands: InputCommand[];
  /** Newest server tick the client has applied, for delta/interp bookkeeping. */
  ackTick: number;
}

export interface PingMessage {
  type: typeof ClientMessage.Ping;
  clientTimeMs: number;
}

export interface ChatMessage {
  type: typeof ClientMessage.Chat;
  text: string;
}

/** Locks in a weapon for the coming round, and marks the player ready. */
export interface SelectLoadoutMessage {
  type: typeof ClientMessage.SelectLoadout;
  weapon: WeaponId;
}

/** Votes for an arena map while standing on a lobby pad. Index into MAP_ROTATION. */
export interface VoteMapMessage {
  type: typeof ClientMessage.VoteMap;
  mapIndex: number;
}

/** Offers to start the pad match outnumbered (e.g. 1v2). */
export interface PadReadyMessage {
  type: typeof ClientMessage.PadReady;
}

/** Votes bot difficulty on a vs-bot pad. Index into BOT_TIERS. */
export interface VoteDifficultyMessage {
  type: typeof ClientMessage.VoteDifficulty;
  tier: number;
}

/** Votes to run the game back. Only counts during the `ended` phase. */
export interface RematchMessage {
  type: typeof ClientMessage.Rematch;
}

/** Asks to be walked back to the lobby, leaving the current match. */
export interface LeaveMatchMessage {
  type: typeof ClientMessage.LeaveMatch;
}

/**
 * WebRTC signalling for voice chat, addressed to one other player. Voice
 * audio itself is peer-to-peer and never touches the server; the server only
 * relays these frames — and only between players in the same match, which is
 * what scopes voice to the room. The payload is opaque JSON (offer/answer/ICE).
 */
export interface VoiceSignalMessage {
  type: typeof ClientMessage.VoiceSignal;
  to: PlayerId;
  data: string;
}

export type AnyClientMessage =
  | JoinMessage
  | InputMessage
  | PingMessage
  | ChatMessage
  | SelectLoadoutMessage
  | VoteMapMessage
  | PadReadyMessage
  | VoteDifficultyMessage
  | RematchMessage
  | LeaveMatchMessage
  | VoiceSignalMessage;

export const encodeJoin = (name: string, character: number, session = ''): Uint8Array =>
  new Writer(128)
    .u8(ClientMessage.Join)
    .u16(PROTOCOL_VERSION)
    .u8(character)
    .string(name)
    .string(session)
    .finish();

/**
 * Sends the last `INPUT_REDUNDANCY` commands every tick. Losing a datagram then
 * costs nothing: the next one carries the missing command, so the server never
 * has to ask for a retransmit.
 */
export const encodeInput = (
  tick: number,
  commands: readonly InputCommand[],
  ackTick: number,
): Uint8Array => {
  const send = commands.slice(-INPUT_REDUNDANCY);
  const writer = new Writer(64);
  writer.u8(ClientMessage.Input);
  writer.u32(tick);
  writer.u32(ackTick);
  writer.u8(send.length);
  for (const command of send) {
    writer.u32(command.seq);
    writer.u16(command.buttons);
    writer.u16(packAngle(command.yaw));
    writer.u16(packAngle(command.pitch));
    writer.u8(command.weapon);
  }
  return writer.finish();
};

export const encodePing = (clientTimeMs: number): Uint8Array =>
  new Writer(16).u8(ClientMessage.Ping).f64(clientTimeMs).finish();

export const encodeChat = (text: string): Uint8Array =>
  new Writer(256).u8(ClientMessage.Chat).string(text.slice(0, 200)).finish();

export const encodeSelectLoadout = (weapon: WeaponId): Uint8Array =>
  new Writer(8).u8(ClientMessage.SelectLoadout).u8(weaponIndex(weapon)).finish();

export const encodeVoteMap = (mapIndex: number): Uint8Array =>
  new Writer(8).u8(ClientMessage.VoteMap).u8(mapIndex).finish();

export const encodePadReady = (): Uint8Array =>
  new Writer(4).u8(ClientMessage.PadReady).finish();

export const encodeVoteDifficulty = (tier: number): Uint8Array =>
  new Writer(8).u8(ClientMessage.VoteDifficulty).u8(tier).finish();

export const encodeRematch = (): Uint8Array =>
  new Writer(4).u8(ClientMessage.Rematch).finish();

export const encodeLeaveMatch = (): Uint8Array =>
  new Writer(4).u8(ClientMessage.LeaveMatch).finish();

export const encodeVoiceSignal = (to: PlayerId, data: string): Uint8Array =>
  new Writer(1024).u8(ClientMessage.VoiceSignal).u16(to).string(data).finish();

export const decodeClientMessage = (bytes: Uint8Array): AnyClientMessage | null => {
  const reader = new Reader(bytes);
  const type = reader.u8();
  switch (type) {
    case ClientMessage.Join:
      return {
        type,
        protocol: reader.u16(),
        character: reader.u8(),
        name: reader.string(),
        session: reader.string(),
      };
    case ClientMessage.Input: {
      const tick = reader.u32();
      const ackTick = reader.u32();
      const count = reader.u8();
      const commands: InputCommand[] = [];
      for (let i = 0; i < count; i++) {
        commands.push({
          seq: reader.u32(),
          buttons: reader.u16(),
          yaw: unpackAngle(reader.u16()),
          pitch: unpackAngle(reader.u16()),
          weapon: reader.u8(),
        });
      }
      return { type, tick, ackTick, commands };
    }
    case ClientMessage.Ping:
      return { type, clientTimeMs: reader.f64() };
    case ClientMessage.Chat:
      return { type, text: reader.string() };
    case ClientMessage.SelectLoadout:
      return { type, weapon: weaponFromIndex(reader.u8()) };
    case ClientMessage.VoteMap:
      return { type, mapIndex: reader.u8() };
    case ClientMessage.PadReady:
      return { type };
    case ClientMessage.VoteDifficulty:
      return { type, tier: reader.u8() };
    case ClientMessage.Rematch:
      return { type };
    case ClientMessage.LeaveMatch:
      return { type };
    case ClientMessage.VoiceSignal:
      return { type, to: reader.u16(), data: reader.string() };
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Server -> Client
// ---------------------------------------------------------------------------

export interface WelcomeMessage {
  type: typeof ServerMessage.Welcome;
  playerId: PlayerId;
  team: TeamId;
  mapId: string;
  tick: number;
  tickRate: number;
  /**
   * Weapon slot the player's seat is holding. Fresh joins get the default;
   * a mid-match reattach (reload, device switch) gets whatever the frozen
   * character was carrying, so prediction doesn't yank it back to slot 1.
   */
  weapon: number;
}

/** One player's state as it appears in a snapshot. */
export interface SnapshotPlayer {
  id: PlayerId;
  team: TeamId;
  position: Vec3;
  yaw: number;
  pitch: number;
  health: number;
  flags: number;
  weapon: WeaponId;
  kills: number;
  deaths: number;
}

export const SnapshotFlag = {
  Alive: 1 << 0,
  OnGround: 1 << 1,
  Crouching: 1 << 2,
  Sliding: 1 << 3,
  Firing: 1 << 4,
} as const;

export interface SnapshotMessage {
  type: typeof ServerMessage.Snapshot;
  tick: number;
  /** Highest input sequence the server has consumed from this client. */
  ackSeq: number;
  /** Authoritative state of the receiving client, for reconciliation. */
  self: {
    position: Vec3;
    velocity: Vec3;
    health: number;
    ammo: number;
    flags: number;
    kills: number;
    deaths: number;
  };
  players: SnapshotPlayer[];
}

export interface EventsMessage {
  type: typeof ServerMessage.Events;
  tick: number;
  events: SimEvents;
}

export interface PongMessage {
  type: typeof ServerMessage.Pong;
  clientTimeMs: number;
  serverTick: number;
}

export interface PlayerJoinedMessage {
  type: typeof ServerMessage.PlayerJoined;
  playerId: PlayerId;
  team: TeamId;
  /** Index into CHARACTERS, already clamped by the server. */
  character: number;
  name: string;
}

export interface PlayerLeftMessage {
  type: typeof ServerMessage.PlayerLeft;
  playerId: PlayerId;
}

export interface ServerChatMessage {
  type: typeof ServerMessage.Chat;
  playerId: PlayerId;
  /**
   * Sender identity travels with the message rather than being resolved from
   * the roster: the server replays recent chat to joiners, and by then the
   * sender may have left (or never been seen by this client at all).
   */
  team: TeamId;
  name: string;
  text: string;
}

export interface MatchStateMessage extends MatchStatus {
  type: typeof ServerMessage.MatchState;
}

/**
 * Queue state of every pad group in the lobby. Groups are referenced by index
 * into `padGroups(map)` — both sides derive the same ordered list from the same
 * map data, so the wire never has to carry group ids or capacities.
 */
export interface HubStateMessage {
  type: typeof ServerMessage.HubState;
  groups: HubGroupStatus[];
}

/** A relayed voice-signalling frame; `from` is who to answer. */
export interface ServerVoiceSignalMessage {
  type: typeof ServerMessage.VoiceSignal;
  from: PlayerId;
  data: string;
}

export interface ErrorMessage {
  type: typeof ServerMessage.Error;
  reason: string;
}

export type AnyServerMessage =
  | WelcomeMessage
  | SnapshotMessage
  | EventsMessage
  | PongMessage
  | PlayerJoinedMessage
  | PlayerLeftMessage
  | ServerChatMessage
  | MatchStateMessage
  | HubStateMessage
  | ServerVoiceSignalMessage
  | ErrorMessage;

export const playerFlags = (player: PlayerState, firing: boolean): number =>
  (player.alive ? SnapshotFlag.Alive : 0) |
  (player.onGround ? SnapshotFlag.OnGround : 0) |
  (player.crouching ? SnapshotFlag.Crouching : 0) |
  (player.sliding ? SnapshotFlag.Sliding : 0) |
  (firing ? SnapshotFlag.Firing : 0);

export const encodeWelcome = (message: Omit<WelcomeMessage, 'type'>): Uint8Array =>
  new Writer(64)
    .u8(ServerMessage.Welcome)
    .u16(message.playerId)
    .u8(message.team)
    .u32(message.tick)
    .u8(message.tickRate)
    .u8(message.weapon)
    .string(message.mapId)
    .finish();

export const encodeSnapshot = (
  tick: number,
  ackSeq: number,
  self: SnapshotMessage['self'],
  players: readonly SnapshotPlayer[],
): Uint8Array => {
  const writer = new Writer(512);
  writer.u8(ServerMessage.Snapshot);
  writer.u32(tick);
  writer.u32(ackSeq);

  // Own state is sent at full precision: reconciliation compares it directly
  // against locally predicted state, so quantisation error would look like drift.
  writer.f32(self.position.x).f32(self.position.y).f32(self.position.z);
  writer.f32(self.velocity.x).f32(self.velocity.y).f32(self.velocity.z);
  writer.u8(Math.max(0, Math.min(255, Math.round(self.health))));
  writer.u8(self.ammo);
  writer.u8(self.flags);
  writer.u16(self.kills);
  writer.u16(self.deaths);

  writer.u8(players.length);
  for (const player of players) {
    writer.u16(player.id);
    writer.u8(player.team);
    writer.i16(packPosition(player.position.x));
    writer.i16(packPosition(player.position.y));
    writer.i16(packPosition(player.position.z));
    writer.u16(packAngle(player.yaw));
    writer.u16(packAngle(player.pitch));
    writer.u8(Math.max(0, Math.min(255, Math.round(player.health))));
    writer.u8(player.flags);
    writer.u8(weaponIndex(player.weapon));
    writer.u16(player.kills);
    writer.u16(player.deaths);
  }
  return writer.finish();
};

export const encodeEvents = (tick: number, events: SimEvents): Uint8Array => {
  const writer = new Writer(512);
  writer.u8(ServerMessage.Events);
  writer.u32(tick);

  const shots = events.shots.slice(0, 255);
  writer.u8(shots.length);
  for (const shot of shots) {
    writer.u16(shot.shooter);
    writer.i16(packPosition(shot.origin.x));
    writer.i16(packPosition(shot.origin.y));
    writer.i16(packPosition(shot.origin.z));
    writer.i16(packPosition(shot.end.x));
    writer.i16(packPosition(shot.end.y));
    writer.i16(packPosition(shot.end.z));
    writer.u8(weaponIndex(shot.weapon));
  }

  const hits = events.hits.slice(0, 255);
  writer.u8(hits.length);
  for (const hit of hits) {
    writer.u16(hit.attacker);
    writer.u16(hit.victim);
    writer.u8(Math.min(255, Math.round(hit.damage)));
    writer.u8((hit.headshot ? 1 : 0) | (hit.killed ? 2 : 0));
    writer.i16(packPosition(hit.point.x));
    writer.i16(packPosition(hit.point.y));
    writer.i16(packPosition(hit.point.z));
  }
  return writer.finish();
};

export const encodePong = (clientTimeMs: number, serverTick: number): Uint8Array =>
  new Writer(24).u8(ServerMessage.Pong).f64(clientTimeMs).u32(serverTick).finish();

export const encodePlayerJoined = (
  playerId: PlayerId,
  team: TeamId,
  character: number,
  name: string,
): Uint8Array =>
  new Writer(64)
    .u8(ServerMessage.PlayerJoined)
    .u16(playerId)
    .u8(team)
    .u8(character)
    .string(name)
    .finish();

export const encodePlayerLeft = (playerId: PlayerId): Uint8Array =>
  new Writer(8).u8(ServerMessage.PlayerLeft).u16(playerId).finish();

export const encodeServerChat = (
  playerId: PlayerId,
  team: TeamId,
  name: string,
  text: string,
): Uint8Array =>
  new Writer(320)
    .u8(ServerMessage.Chat)
    .u16(playerId)
    .u8(team)
    .string(name)
    .string(text)
    .finish();

export const encodeHubState = (groups: readonly HubGroupStatus[]): Uint8Array => {
  const writer = new Writer(256);
  writer.u8(ServerMessage.HubState);
  writer.u8(groups.length);
  for (const group of groups) {
    writer.u32(Math.max(0, Math.round(group.countdownEndsInMs)));
    for (const team of group.teams) {
      writer.u8(team.length);
      for (const id of team) writer.u16(id);
    }
    writer.u8(group.votes.length);
    for (const vote of group.votes) {
      writer.u16(vote.id);
      writer.u8(vote.mapIndex);
    }
    writer.u8(group.difficultyVotes.length);
    for (const vote of group.difficultyVotes) {
      writer.u16(vote.id);
      writer.u8(vote.tier);
    }
    writer.u8(group.ready.length);
    for (const id of group.ready) writer.u16(id);
  }
  return writer.finish();
};

export const encodeMatchState = (status: MatchStatus): Uint8Array => {
  const writer = new Writer(256);
  writer.u8(ServerMessage.MatchState);
  writer.u8(matchPhaseIndex(status.phase));
  writer.u32(Math.max(0, Math.round(status.phaseEndsInMs)));
  writer.u16(status.scores[0]);
  writer.u16(status.scores[1]);
  writer.u8(status.rounds[0]);
  writer.u8(status.rounds[1]);
  writer.u8(Math.min(255, status.roundNumber));
  writer.u8(status.lastRoundResult);
  const roster = status.roster.slice(0, 255);
  writer.u8(roster.length);
  for (const entry of roster) {
    writer.u16(entry.id);
    writer.u8(entry.team);
    writer.u8(entry.ready ? 1 : 0);
  }
  // Last, because it is the only variable-length field.
  writer.string(status.mapId);
  return writer.finish();
};

export const encodeServerVoiceSignal = (from: PlayerId, data: string): Uint8Array =>
  new Writer(1024).u8(ServerMessage.VoiceSignal).u16(from).string(data).finish();

export const encodeError = (reason: string): Uint8Array =>
  new Writer(128).u8(ServerMessage.Error).string(reason).finish();

export const decodeServerMessage = (bytes: Uint8Array): AnyServerMessage | null => {
  const reader = new Reader(bytes);
  const type = reader.u8();
  switch (type) {
    case ServerMessage.Welcome:
      return {
        type,
        playerId: reader.u16(),
        team: reader.u8() as TeamId,
        tick: reader.u32(),
        tickRate: reader.u8(),
        weapon: reader.u8(),
        mapId: reader.string(),
      };
    case ServerMessage.Snapshot: {
      const tick = reader.u32();
      const ackSeq = reader.u32();
      const self = {
        position: vec3(reader.f32(), reader.f32(), reader.f32()),
        velocity: vec3(reader.f32(), reader.f32(), reader.f32()),
        health: reader.u8(),
        ammo: reader.u8(),
        flags: reader.u8(),
        kills: reader.u16(),
        deaths: reader.u16(),
      };
      const count = reader.u8();
      const players: SnapshotPlayer[] = [];
      for (let i = 0; i < count; i++) {
        players.push({
          id: reader.u16(),
          team: reader.u8() as TeamId,
          position: vec3(
            unpackPosition(reader.i16()),
            unpackPosition(reader.i16()),
            unpackPosition(reader.i16()),
          ),
          yaw: unpackAngle(reader.u16()),
          pitch: unpackAngle(reader.u16()),
          health: reader.u8(),
          flags: reader.u8(),
          weapon: weaponFromIndex(reader.u8()),
          kills: reader.u16(),
          deaths: reader.u16(),
        });
      }
      return { type, tick, ackSeq, self, players };
    }
    case ServerMessage.Events: {
      const tick = reader.u32();
      const shotCount = reader.u8();
      const events: SimEvents = { shots: [], hits: [] };
      for (let i = 0; i < shotCount; i++) {
        events.shots.push({
          shooter: reader.u16(),
          origin: vec3(
            unpackPosition(reader.i16()),
            unpackPosition(reader.i16()),
            unpackPosition(reader.i16()),
          ),
          end: vec3(
            unpackPosition(reader.i16()),
            unpackPosition(reader.i16()),
            unpackPosition(reader.i16()),
          ),
          weapon: weaponFromIndex(reader.u8()),
        });
      }
      const hitCount = reader.u8();
      for (let i = 0; i < hitCount; i++) {
        const attacker = reader.u16();
        const victim = reader.u16();
        const damage = reader.u8();
        const bits = reader.u8();
        events.hits.push({
          attacker,
          victim,
          damage,
          headshot: (bits & 1) !== 0,
          killed: (bits & 2) !== 0,
          point: vec3(
            unpackPosition(reader.i16()),
            unpackPosition(reader.i16()),
            unpackPosition(reader.i16()),
          ),
        });
      }
      return { type, tick, events };
    }
    case ServerMessage.Pong:
      return { type, clientTimeMs: reader.f64(), serverTick: reader.u32() };
    case ServerMessage.PlayerJoined:
      return {
        type,
        playerId: reader.u16(),
        team: reader.u8() as TeamId,
        character: reader.u8(),
        name: reader.string(),
      };
    case ServerMessage.PlayerLeft:
      return { type, playerId: reader.u16() };
    case ServerMessage.Chat:
      return {
        type,
        playerId: reader.u16(),
        team: reader.u8() as TeamId,
        name: reader.string(),
        text: reader.string(),
      };
    case ServerMessage.MatchState: {
      const phase = matchPhaseFromIndex(reader.u8());
      const phaseEndsInMs = reader.u32();
      const scores: [number, number] = [reader.u16(), reader.u16()];
      const rounds: [number, number] = [reader.u8(), reader.u8()];
      const roundNumber = reader.u8();
      const lastRoundResult = reader.u8();
      const count = reader.u8();
      const roster: LobbyEntry[] = [];
      for (let i = 0; i < count; i++) {
        roster.push({
          id: reader.u16(),
          team: reader.u8() as TeamId,
          ready: reader.u8() === 1,
        });
      }
      return {
        type,
        phase,
        phaseEndsInMs,
        scores,
        rounds,
        roundNumber,
        lastRoundResult,
        roster,
        mapId: reader.string(),
      };
    }
    case ServerMessage.HubState: {
      const groupCount = reader.u8();
      const groups: HubGroupStatus[] = [];
      for (let g = 0; g < groupCount; g++) {
        const countdownEndsInMs = reader.u32();
        const teams: [PlayerId[], PlayerId[]] = [[], []];
        for (const team of teams) {
          const count = reader.u8();
          for (let i = 0; i < count; i++) team.push(reader.u16());
        }
        const votes: HubGroupStatus['votes'] = [];
        const voteCount = reader.u8();
        for (let i = 0; i < voteCount; i++) {
          votes.push({ id: reader.u16(), mapIndex: reader.u8() });
        }
        const difficultyVotes: HubGroupStatus['difficultyVotes'] = [];
        const difficultyCount = reader.u8();
        for (let i = 0; i < difficultyCount; i++) {
          difficultyVotes.push({ id: reader.u16(), tier: reader.u8() });
        }
        const ready: PlayerId[] = [];
        const readyCount = reader.u8();
        for (let i = 0; i < readyCount; i++) ready.push(reader.u16());
        groups.push({ countdownEndsInMs, teams, votes, difficultyVotes, ready });
      }
      return { type, groups };
    }
    case ServerMessage.VoiceSignal:
      return { type, from: reader.u16(), data: reader.string() };
    case ServerMessage.Error:
      return { type, reason: reader.string() };
    default:
      return null;
  }
};

export const clampHealth = (health: number): number =>
  Math.max(0, Math.min(PLAYER_MAX_HEALTH, health));
